import {
  RefObject,
  useCallback,
  useRef,
  useState,
} from 'react';

/** The scene the caret is in, as the script spells it. */
export type CaretScene = {
  name: string | null;
  location: string | null;
  line: number;
};

/**
 * The caret's 1-based LOGICAL line — `value.slice(0, selectionStart).split('\n').length`
 * (editor.js:154-156). Not a visual row: a wrapped line is one line here, which is what the gutter,
 * the band and every drawer label agree on.
 */
export function cursorLine(value: string, selectionStart: number): number {
  return String(value ?? '').slice(0, Math.max(0, selectionStart)).split('\n').length;
}

/**
 * The last `== NAME [@ location]` heading at or above a line (editor.js:158-173). This is the scene
 * as the writer wrote it, which is the only scene name that may reach a card.
 */
export function sceneAt(text: string, lineNumber: number): CaretScene {
  const lines = String(text ?? '').replace(/\r\n?/g, '\n').split('\n');
  let scene: CaretScene = { name: null, location: null, line: 1 };
  for (let index = 0; index < Math.min(lineNumber, lines.length); index += 1) {
    const matched = lines[index].match(/^\s*==\s*(.*?)\s*$/);
    if (matched) {
      const at = matched[1].lastIndexOf(' @ ');
      scene = {
        name: (at < 0 ? matched[1] : matched[1].slice(0, at)).trim(),
        location: at < 0 ? null : matched[1].slice(at + 3).trim(),
        line: index + 1,
      };
    }
  }

  return scene;
}

/** What the caret hook hands back. */
export type CaretContext = {
  /** The 1-based logical line the caret is on. */
  currentLine: number;
  /** Recompute from the textarea; cheap, synchronous, never debounced. */
  refresh: () => void;
  /** Put the caret on a line's first character and band it. */
  goToLine: (line: number) => void;
};

/**
 * Everything caret-driven is synchronous and never debounced (spec §4.5). The band repaints only when
 * the line NUMBER changes, so an arrow key inside one line costs nothing.
 */
export function useCaretContext(textareaRef: RefObject<HTMLTextAreaElement>): CaretContext {
  const [currentLine, setCurrentLine] = useState(1);
  const lineRef = useRef(1);

  const refresh = useCallback(() => {
    const node = textareaRef.current;
    if (!node) {
      return;
    }
    const at = cursorLine(node.value, node.selectionStart ?? 0);
    if (at === lineRef.current) {
      return;
    }
    lineRef.current = at;
    setCurrentLine(at);
  }, [textareaRef]);

  const goToLine = useCallback((line: number) => {
    const node = textareaRef.current;
    if (!node) {
      return;
    }
    const lines = node.value.replace(/\r\n?/g, '\n').split('\n');
    const target = Math.min(Math.max(1, Math.round(line)), lines.length);
    const offset = lines.slice(0, target - 1).reduce((total, one) => total + one.length + 1, 0);
    node.focus({ preventScroll: true });
    node.setSelectionRange(offset, offset);
    lineRef.current = target;
    setCurrentLine(target);
  }, [textareaRef]);

  return { currentLine, refresh, goToLine };
}
